import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Sparkles, Binoculars, Crown, Heart, FlaskConical, Wrench } from "lucide-react";

const QUESTIONS = [
  {
    q: "It's inspection day. You arrive at the apiary with…",
    answers: [
      { text: "A notebook and a hundred questions", bee: "scout" },
      { text: "A plan — and last season's notes", bee: "queenie" },
      { text: "Snacks for everyone helping", bee: "honey" },
      { text: "A varroa kit and a spreadsheet", bee: "drbuzz" },
      { text: "A smoker and five spare minutes", bee: "keeper" },
    ],
  },
  {
    q: "Your favorite part of beekeeping?",
    answers: [
      { text: "Learning something new every single time", bee: "scout" },
      { text: "Watching a colony thrive under my care", bee: "queenie" },
      { text: "Sharing honey with friends and neighbors", bee: "honey" },
      { text: "The data — counts, trends, patterns", bee: "drbuzz" },
      { text: "Fixing problems with my own hands", bee: "keeper" },
    ],
  },
  {
    q: "A hive looks queenless. You…",
    answers: [
      { text: "Ask a more experienced beekeeper first", bee: "scout" },
      { text: "Study the brood pattern before deciding", bee: "queenie" },
      { text: "Stay calm — the bees have survived worse", bee: "honey" },
      { text: "Work through the possibilities systematically", bee: "drbuzz" },
      { text: "Add a frame of eggs and let them sort it out", bee: "keeper" },
    ],
  },
];

const RESULTS = {
  scout: { icon: Binoculars, name: "Scout", role: "Friendly Beginner", copy: "Curious, open, and learning fast — exactly how every great beekeeper starts. Scout will walk beside you, one patient answer at a time." },
  queenie: { icon: Crown, name: "Queenie", role: "Experienced Mentor", copy: "Steady, observant, and colony-first. Queenie speaks your language — measured, wise, and focused on strong, healthy hives." },
  honey: { icon: Heart, name: "Honey", role: "Positive & Cheerful", copy: "You keep bees for the joy of it — and it shows. Honey will celebrate every win with you, from first comb to first full super." },
  drbuzz: { icon: FlaskConical, name: "Dr. Buzz", role: "Scientific Expert", copy: "Counts, trends, evidence. You don't guess — you measure. Dr. Buzz brings the research and the receipts." },
  keeper: { icon: Wrench, name: "Keeper", role: "Practical Pro", copy: "No fuss, no panic — just solutions. Keeper matches your pace: practical, hands-on, and ready for whatever the season brings." },
};

export default function CompanionQuiz() {
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState([]);
  const done = step >= QUESTIONS.length;

  const answer = (bee) => {
    setPicks((p) => [...p, bee]);
    setStep((s) => s + 1);
  };

  const restart = () => {
    setPicks([]);
    setStep(0);
  };

  let result = null;
  if (done) {
    const tally = {};
    picks.forEach((b) => { tally[b] = (tally[b] || 0) + 1; });
    const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0][0];
    result = RESULTS[top];
  }

  return (
    <div className="bg-forest rounded-[2rem] p-8 md:p-12 text-bone" data-testid="companion-quiz">
      <AnimatePresence mode="wait">
        {!done ? (
          <motion.div
            key={`q-${step}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-[10px] uppercase tracking-[0.25em] text-honey mb-3" data-testid="quiz-progress">
              Which bee are you? · Question {step + 1} of {QUESTIONS.length}
            </p>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-7" data-testid="quiz-question">
              {QUESTIONS[step].q}
            </h3>
            <div className="flex flex-col gap-3">
              {QUESTIONS[step].answers.map((a, i) => (
                <button
                  key={a.text}
                  type="button"
                  onClick={() => answer(a.bee)}
                  data-testid={`quiz-answer-${step}-${i}`}
                  className="text-left text-sm md:text-base bg-bone/5 border border-bone/15 rounded-xl px-5 py-3.5 transition-colors duration-300 hover:border-honey hover:bg-honey/10"
                >
                  {a.text}
                </button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
            data-testid="quiz-result"
          >
            <Sparkles className="w-7 h-7 text-honey mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[10px] uppercase tracking-[0.25em] text-honey mb-3">You're most like</p>
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-honey/15 mb-4">
              <result.icon className="w-8 h-8 text-honey" strokeWidth={1.5} />
            </span>
            <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight" data-testid="quiz-result-name">
              {result.name}
            </h3>
            <p className="text-xs uppercase tracking-[0.2em] text-bone/50 mt-1.5 mb-5">{result.role}</p>
            <p className="text-bone/70 leading-relaxed max-w-md mx-auto mb-8">{result.copy}</p>
            <p className="text-xs text-bone/40 mb-6">
              Ask a Bee™ lets you pick your companion at launch — or change their voice anytime.
            </p>
            <button
              type="button"
              onClick={restart}
              data-testid="quiz-restart-button"
              className="inline-flex items-center gap-2 text-sm font-semibold text-honey border border-honey/40 px-6 py-3 rounded-full transition-colors duration-300 hover:bg-honey hover:text-forest"
            >
              <RotateCcw className="w-4 h-4" />
              Take it again
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
